import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import {
  Save,
  FileText,
  CheckCircle,
  Loader2,
  ArrowLeft,
  ArrowRight,
  Trash2,
  Info,
  Tag,
  FolderOpen
} from 'lucide-react';
import { OptimizationResult } from '../shared/types';
import { STEP_MOTION_CLASS, STEP_CARD_COMPACT_CLASS, STEP_WRAPPER_CLASS, STEP_SCROLL_AREA_CLASS, STEP_NAV_CLASS } from '../shared/stepLayout';
import { ApplyTrackModal } from '../../optimize/ApplyTrackModal';
import { saveJobApplication } from '@soVendor/app/services/jobApplicationService';
import { supabase } from '@soVendor/lib/supabase';

interface SaveHistoryStepProps {
  onNext: () => void;
  onBack: () => void;
  optimizationResult: OptimizationResult;
  onSave: (data: {
    versionName: string;
    tags: string[];
    retentionPolicy: '30days' | 'indefinite';
    description?: string;
  }) => Promise<string>;
  isLoading: boolean;
  error: string | null;
  skipEnabled?: boolean;
  skipMessage?: string;
}

const SaveHistoryStep: React.FC<SaveHistoryStepProps> = ({
  onNext,
  onBack,
  optimizationResult,
  onSave,
  isLoading,
  skipEnabled = false,
  skipMessage
}) => {
  const { t } = useTranslation();
  const isNative = Capacitor.isNativePlatform();
  const [versionName, setVersionName] = useState(`Resume - ${new Date().toLocaleDateString()}`);
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [retentionPolicy, setRetentionPolicy] = useState<'30days' | 'indefinite'>('30days');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showApplyTrackModal, setShowApplyTrackModal] = useState(false);
  const [session, setSession] = useState<{ user?: { id: string } } | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => setSession(s ?? null));
  }, []);

  const suggestedTags = [
    t('optimizer.saveHistoryStep.suggestedTag1'),
    t('optimizer.saveHistoryStep.suggestedTag2'),
    t('optimizer.saveHistoryStep.suggestedTag3'),
    t('optimizer.saveHistoryStep.suggestedTag4'),
    t('optimizer.saveHistoryStep.suggestedTag5')
  ];

  const addTag = (tag: string) => {
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        versionName,
        tags,
        retentionPolicy,
        description
      });
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndContinue = async () => {
    if (!saved) {
      await handleSave();
      setShowApplyTrackModal(true);
    } else {
      onNext();
    }
  };

  const handleSkipAndContinue = () => {
    // Allow user to skip saving and go directly to next step
    onNext();
  };

  const primaryButtonClass = 'bg-[var(--vscode-primary)] text-[var(--vscode-primary-text)] hover:opacity-90 transition-opacity font-semibold';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.3 }}
      className={isNative ? '' : STEP_MOTION_CLASS}
      style={isNative ? { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', isolation: 'isolate' as const } : undefined}
    >
      {/* Native splash header */}
      {isNative && (
        <div
          className="flex-shrink-0 flex items-center justify-between px-4 pb-3"
          style={{
            background: 'linear-gradient(135deg,#6C47FF 0%,#4F35CC 100%)',
            paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 40px)',
          }}
        >
          <button type="button" onClick={onBack} className="w-9 h-9 flex items-center justify-center rounded-xl touch-manipulation" style={{ background: 'var(--ggj-header-btn-bg)', color: 'var(--ggj-header-btn-icon)' }}>
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex flex-col items-center">
            <span className="text-[10px] font-semibold text-purple-200 uppercase tracking-widest">Resume Optimizer</span>
            <span className="text-base font-bold text-white leading-tight">Save Resume</span>
          </div>
          <div className="w-9 h-9" />
        </div>
      )}
      {/* Warning Banner for Guest Optimizations */}
      {skipEnabled && skipMessage && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-1">
                {t('optimizer.saveHistoryStep.guestBannerTitle')}
              </h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                {skipMessage}
              </p>
              <button
                onClick={handleSkipAndContinue}
                className="mt-2 text-sm font-medium text-yellow-800 dark:text-yellow-200 hover:text-yellow-900 dark:hover:text-yellow-100 underline"
              >
                {t('optimizer.saveHistoryStep.skipToNextStep')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Card: scrollable area so nav stays visible on mobile/tablet */}
      <div className={isNative ? 'flex-1 min-h-0 overflow-hidden flex flex-col' : STEP_WRAPPER_CLASS}>
        <div className={isNative ? 'flex-1 overflow-y-auto scrollbar-hide px-4 py-3' : STEP_SCROLL_AREA_CLASS}>
          <div className={`${isNative ? '' : STEP_CARD_COMPACT_CLASS} w-full max-w-full`}>
            <div className="grid grid-cols-1 lg:grid-cols-[1.75fr_0.85fr] gap-0">
            {/* Left: Save options */}
            <div className="p-4 sm:p-5 lg:p-6 flex flex-col">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center vscode-bg-tertiary flex-shrink-0">
              <Save className="w-4 h-4" style={{ color: 'var(--vscode-primary)' }} />
            </div>
            <div>
              <h3 className="text-base font-semibold vscode-text-primary">Save to History</h3>
              <p className="text-xs vscode-text-tertiary">Step 8 of 12</p>
            </div>
          </div>

          <div className="space-y-2.5">
            <div>
              <label className="text-xs font-medium vscode-text-secondary mb-0.5 block">Version Name</label>
              <input
                type="text"
                value={versionName}
                onChange={(e) => setVersionName(e.target.value)}
                className="w-full px-3 py-3 min-h-[44px] text-sm vscode-bg-tertiary border vscode-border rounded-lg vscode-text-primary focus:outline-none touch-manipulation"
                placeholder="e.g., Resume - Software Engineer"
                style={{ borderWidth: '1px' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--vscode-primary)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--vscode-border-primary)'; }}
              />
            </div>

            <div>
              <label className="text-xs font-medium vscode-text-secondary mb-0.5 block">{t('optimizer.saveHistoryStep.descriptionOptional')}</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 text-sm vscode-bg-tertiary border vscode-border rounded-lg vscode-text-primary focus:outline-none"
                placeholder={t('optimizer.saveHistoryStep.descriptionPlaceholder')}
                style={{ borderWidth: '1px' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--vscode-primary)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--vscode-border-primary)'; }}
              />
            </div>

            <div>
              <label className="text-xs font-medium vscode-text-secondary mb-0.5 block">Tags</label>
              <div className="flex flex-col sm:flex-row gap-2 mb-0.5">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addTag(newTag)}
                  className="flex-1 min-h-[44px] px-3 py-2 sm:py-1.5 text-sm vscode-bg-tertiary border vscode-border rounded-lg vscode-text-primary focus:outline-none touch-manipulation"
                  placeholder="Add tag..."
                  style={{ borderWidth: '1px' }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--vscode-primary)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--vscode-border-primary)'; }}
                />
                <button
                  onClick={() => addTag(newTag)}
                  className={`min-h-[44px] px-4 py-2 sm:py-1.5 text-sm rounded-lg shrink-0 touch-manipulation ${primaryButtonClass}`}
                >
                  {t('optimizer.saveHistoryStep.add')}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {suggestedTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => addTag(tag)}
                    className="min-h-[44px] min-w-[44px] px-3 py-2 sm:py-1.5 vscode-bg-tertiary hover:vscode-bg-hover vscode-text-secondary text-xs rounded border vscode-border transition-colors touch-manipulation"
                  >
                    +{tag}
                  </button>
                ))}
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-1.5 py-0.5 text-xs rounded border gap-1"
                      style={{
                        backgroundColor: 'var(--vscode-primary-bg)',
                        color: 'var(--vscode-primary)',
                        borderColor: 'var(--vscode-primary-border)'
                      }}
                    >
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)} className="hover:opacity-80" style={{ color: 'var(--vscode-error)' }}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

            {/* Right: Cover – Quick tips only — hidden on native */}
            <div className={`p-4 sm:p-5 lg:p-6 vscode-bg-tertiary/30 border-t lg:border-t-0 lg:border-l vscode-border flex flex-col min-w-0 w-full ${isNative ? 'hidden' : ''}`}>
              <h3 className="text-sm font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--vscode-primary)' }}>
                Quick tips
              </h3>
              <ul className="space-y-1.5 mb-3 text-xs vscode-text-secondary">
                <li className="flex items-start gap-2">
                  <Tag className="w-4 h-4 shrink-0 mt-0.5" style={{ color: 'var(--vscode-primary)' }} />
                  <span>Add tags to find this version later (e.g. role, company).</span>
                </li>
                <li className="flex items-start gap-2">
                  <FolderOpen className="w-4 h-4 shrink-0 mt-0.5" style={{ color: 'var(--vscode-primary)' }} />
                  <span>Save to access anytime, compare versions, and export or share.</span>
                </li>
              </ul>

              <div className="vscode-bg-secondary rounded-lg border vscode-border p-2">
                <p className="text-xs font-medium vscode-text-primary mb-1">This version</p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs vscode-text-tertiary">
                  <span>Original</span>
                  <span className="text-right vscode-text-primary font-medium">{optimizationResult?.beforeScores?.overall ?? 0}%</span>
                  <span>{t('optimizer.saveHistoryStep.optimized')}</span>
                  <span className="text-right font-medium" style={{ color: 'var(--vscode-success)' }}>{optimizationResult?.afterScores?.overall ?? 0}%</span>
                  <span>{t('optimizer.saveHistoryStep.improvement')}</span>
                  <span className="text-right font-medium" style={{ color: 'var(--vscode-info)' }}>
                    +{Math.round(((optimizationResult?.afterScores?.overall ?? 0) - (optimizationResult?.beforeScores?.overall ?? 0)) * 100) / 100}%
                  </span>
                </div>
              </div>
              <p className="text-xs vscode-text-tertiary mt-2">
                Storage: Unlimited · Secure backup · Export & share
              </p>
            </div>
            </div>
          </div>
        </div>

        {/* Bottom navigation */}
        <nav className={isNative ? 'flex-shrink-0 flex items-center gap-2 px-4 pt-2 border-t' : STEP_NAV_CLASS}
          style={isNative ? {
            background: 'var(--ggj-nav-bg)',
            borderColor: 'var(--ggj-border)',
            paddingBottom: 12,
          } : undefined}
          aria-label="Step navigation"
        >
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to Download"
            className="flex items-center justify-center gap-2 min-h-[44px] px-3 sm:px-4 py-3 vscode-text-secondary hover:vscode-text-primary font-medium rounded-lg transition-colors text-sm border vscode-border vscode-bg-secondary hover:opacity-90 touch-manipulation flex-initial min-w-0"
          >
            <ArrowLeft className="w-4 h-4 shrink-0" />
            <span className="hidden sm:inline">{t('optimizer.saveHistoryStep.backToDownload')}</span>
          </button>
          <button
            type="button"
            onClick={handleSkipAndContinue}
            disabled={saving || isLoading}
            className="flex items-center justify-center gap-2 min-h-[44px] px-3 py-3 text-sm vscode-bg-tertiary hover:vscode-bg-hover vscode-text-secondary rounded-lg transition-colors disabled:opacity-50 touch-manipulation flex-initial min-w-0"
          >
            <span className="hidden sm:inline">{t('optimizer.saveHistoryStep.skipForNow')}</span>
            <ArrowRight className="w-4 h-4 shrink-0" />
          </button>
          <button
            type="button"
            onClick={handleSaveAndContinue}
            disabled={saving || isLoading}
            className={`flex items-center justify-center gap-2 min-h-[44px] px-4 sm:px-5 py-3 text-sm rounded-lg transition-opacity touch-manipulation flex-initial min-w-0 ${
              !saving && !isLoading ? primaryButtonClass : 'vscode-bg-tertiary vscode-text-tertiary cursor-not-allowed'
            }`}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : saved ? <CheckCircle className="w-4 h-4 shrink-0" /> : <Save className="w-4 h-4 shrink-0" />}
            <span className="hidden sm:inline">{saving ? t('optimizer.saveHistoryStep.saving') : saved ? t('optimizer.saveHistoryStep.continueToApply') : t('optimizer.saveHistoryStep.saveAndContinue')}</span>
            <ArrowRight className="w-4 h-4 shrink-0 hidden sm:block" />
          </button>
        </nav>
      </div>

      <AnimatePresence>
        <ApplyTrackModal
          isOpen={showApplyTrackModal}
          onClose={() => setShowApplyTrackModal(false)}
          onTrack={async () => {
            if (!session?.user?.id) return;
            const result = await saveJobApplication({
              userId: session.user.id,
              jobTitle: (optimizationResult as any)?.jobTitle || 'Unknown Position',
              companyName: (optimizationResult as any)?.companyName || 'Unknown Company',
              jobUrl: (optimizationResult as any)?.jobUrl || '',
              jobDescription: (optimizationResult as any)?.jobDescription || '',
              resumeVersionId: (optimizationResult as any)?.resumeId || optimizationResult?.resumeId || '',
              status: 'saved'
            });
            if (result.success) {
              setShowApplyTrackModal(false);
              onNext();
            }
          }}
          onSkip={() => {
            setShowApplyTrackModal(false);
            onNext();
          }}
          jobData={{
            jobTitle: (optimizationResult as any)?.jobTitle,
            companyName: (optimizationResult as any)?.companyName,
            jobUrl: (optimizationResult as any)?.jobUrl
          }}
        />
      </AnimatePresence>
    </motion.div>
  );
};

export default React.memo(SaveHistoryStep);

